import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Divider,
  Paper,
  Grid,
  IconButton,
} from '@mui/material';
import {
  QrCodeScanner,
  CheckCircle,
  Warning,
  Info,
  Close,
  Refresh,
  CameraAlt,
  Assignment,
  Build,
  Science,
  Analytics,
  Visibility,
} from '@mui/icons-material';
import { useQRWorkflow } from '../hooks/useQRWorkflow';
import { useAuth } from '../contexts/AuthContext';
import { Sample } from '../services/samplesService';

interface WorkflowQRScannerProps {
  onClose?: () => void;
  onSampleProcessed?: (sample: Sample) => void;
  mode?: 'dialog' | 'page';
}

const WorkflowQRScanner: React.FC<WorkflowQRScannerProps> = ({
  onClose,
  onSampleProcessed,
  mode = 'dialog'
}) => {
  const { user } = useAuth();
  const {
    loading,
    error,
    sample,
    nextAction,
    processQRScan,
    executeWorkflowAction,
    getWorkflowMessage,
    canUserProcessSample,
    reset,
  } = useQRWorkflow();

  const [isScanning, setIsScanning] = useState(false);
  const [manualInput, setManualInput] = useState('');
  const [showManualInput, setShowManualInput] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (isScanning) {
      startCamera();
    } else {
      stopCamera();
    }

    return () => {
      stopCamera();
    };
  }, [isScanning]);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' } // Use back camera if available
      });
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
    } catch (error) {
      console.error('Error accessing camera:', error);
      setIsScanning(false);
    }
  };

  const stopCamera = () => {
    if (videoRef.current?.srcObject) {
      const stream = videoRef.current.srcObject as MediaStream;
      stream.getTracks().forEach(track => track.stop());
    }
  };

  const handleScan = async (qrData: string) => {
    if (!qrData.trim()) return;

    try {
      setIsScanning(false);
      const { sample: scannedSample } = await processQRScan(qrData);
      onSampleProcessed?.(scannedSample);
    } catch (error) {
      console.error('QR scan error:', error);
    }
  };

  const handleManualInput = () => {
    if (manualInput.trim()) {
      handleScan(manualInput.trim());
      setManualInput('');
      setShowManualInput(false);
    }
  };

  const handleProceedWithWorkflow = () => {
    if (sample && nextAction) {
      executeWorkflowAction(sample, nextAction);
      reset();
      onClose?.();
    }
  };

  const handleViewOnly = () => {
    if (sample) {
      executeWorkflowAction(sample, 'view');
      reset();
      onClose?.();
    }
  };

  const handleReset = () => {
    reset();
    setIsScanning(false);
    setManualInput('');
    setShowManualInput(false);
  };

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'reception': return <Assignment color="primary" />;
      case 'preparation': return <Build color="warning" />;
      case 'analysis': return <Science color="secondary" />;
      case 'quality': return <Analytics color="success" />;
      default: return <Visibility color="info" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'RECEPTION': return 'info';
      case 'PREPARATION_MECANIQUE': return 'warning';
      case 'ANALYSE_CHIMIQUE': return 'secondary';
      case 'PRET': return 'success';
      case 'REJETE': return 'error';
      default: return 'default';
    }
  };

  const renderContent = () => (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3, textAlign: 'center' }}>
        <QrCodeScanner sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
        <Typography variant="h5" component="h2" gutterBottom>
          Scanner QR Code
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Scannez le QR code de l'échantillon pour continuer le workflow
        </Typography>
      </Box>

      {/* User Role Info */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Rôle actuel:</strong> {user?.role} - {user?.firstName} {user?.lastName}
        </Typography>
      </Alert>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => reset()}>
          {error}
        </Alert>
      )}

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Sample Result */}
      {sample && !loading && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={8}>
              <Typography variant="h6" gutterBottom>
                {sample.sample_id}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                <Chip
                  label={sample.statut}
                  color={getStatusColor(sample.statut) as any}
                  size="small"
                />
                <Chip
                  label={sample.priorite}
                  variant="outlined"
                  size="small"
                />
                <Chip
                  label={sample.type_echantillon}
                  variant="outlined"
                  size="small"
                />
              </Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Client: {sample.client?.company_name || 'N/A'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Localisation: {sample.localisation_stockage || 'Non définie'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4} sx={{ textAlign: 'center' }}>
              {nextAction && getActionIcon(nextAction)}
              <Typography variant="body2" sx={{ mt: 1 }}>
                {canUserProcessSample(user?.role, sample.statut) ? 'Action disponible' : 'Consultation uniquement'}
              </Typography>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          <Typography variant="body2" sx={{ mb: 3 }}>
            {sample && nextAction && getWorkflowMessage(sample, nextAction)}
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            {canUserProcessSample(user?.role, sample.statut) && nextAction !== 'view' && (
              <Button
                variant="contained"
                startIcon={getActionIcon(nextAction || 'view')}
                onClick={handleProceedWithWorkflow}
                color="primary"
              >
                Continuer le workflow
              </Button>
            )}
            <Button
              variant="outlined"
              startIcon={<Visibility />}
              onClick={handleViewOnly}
            >
              Voir détails
            </Button>
            <Button
              variant="text"
              startIcon={<Refresh />}
              onClick={handleReset}
            >
              Nouveau scan
            </Button>
          </Box>
        </Paper>
      )}

      {/* Scanner Interface */}
      {!sample && !loading && (
        <Box>
          {!isScanning ? (
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Button
                variant="contained"
                size="large"
                startIcon={<CameraAlt />}
                onClick={() => setIsScanning(true)}
                sx={{ mb: 2 }}
              >
                Activer la caméra
              </Button>
              <br />
              <Button
                variant="outlined"
                onClick={() => setShowManualInput(true)}
              >
                Saisie manuelle
              </Button>
            </Box>
          ) : (
            <Box sx={{ position: 'relative', mb: 3 }}>
              <video
                ref={videoRef}
                style={{
                  width: '100%',
                  maxWidth: '400px',
                  height: '300px',
                  objectFit: 'cover',
                  borderRadius: '8px',
                  border: '2px solid #ddd'
                }}
                playsInline
                muted
              />
              <canvas
                ref={canvasRef}
                style={{ display: 'none' }}
              />
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Button
                  variant="outlined"
                  onClick={() => setIsScanning(false)}
                >
                  Arrêter la caméra
                </Button>
              </Box>
            </Box>
          )}

          {/* Manual Input Dialog */}
          <Dialog open={showManualInput} onClose={() => setShowManualInput(false)}>
            <DialogTitle>Saisie manuelle du code</DialogTitle>
            <DialogContent>
              <input
                type="text"
                value={manualInput}
                onChange={(e) => setManualInput(e.target.value)}
                placeholder="Entrez le code de l'échantillon"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '16px',
                  marginTop: '16px'
                }}
                onKeyPress={(e) => e.key === 'Enter' && handleManualInput()}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShowManualInput(false)}>Annuler</Button>
              <Button onClick={handleManualInput} variant="contained">
                Scanner
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      )}
    </Box>
  );

  if (mode === 'dialog') {
    return (
      <Dialog open maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            Scanner QR Code
            {onClose && (
              <IconButton onClick={onClose}>
                <Close />
              </IconButton>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {renderContent()}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Card sx={{ maxWidth: 600, mx: 'auto', mt: 3 }}>
      <CardContent>
        {renderContent()}
      </CardContent>
    </Card>
  );
};

export default WorkflowQRScanner; 